import {
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  Operation,
  createHttpLink,
} from '@apollo/client'
import { withApollo } from 'next-apollo'
import { RetryLink } from '@apollo/client/link/retry'
import { ErrorHandler, onError } from '@apollo/client/link/error'
import { Observable } from 'apollo-link'
import { getUserAndToken } from '@chegg-wt/core/api/user'
import {
  getSessionStorageItem,
  SESSION_STORAGE_KEYS,
} from '@chegg-wt/core/utils/sessionStorage'
import fetch from 'isomorphic-unfetch'
import { Cookies, getCookie } from '@chegg-wt/core/utils/cookies'
import { Config } from '../config'
import { isProduction, isProdByNodeEnv } from '../utils/env'
import { updateUserAndToken, getToken } from '../utils/localStorage'

const APOLLOGRAPH_CLIENT_NAME = 'apollographql-client-name'
const CHEGG_UCF_WRITING_TOKEN = 'chegg-ucf-writing-token'

export interface ResponseError {
  statusCode: number
  bodyText: string
  response?: any
  result?: {
    errors: ApolloServerError[]
  }
}

interface ApolloServerError {
  message: string
  extensions: {
    code: string
  }
}

export const checkStatusCodesRefresh = (error: ResponseError) =>
  error.statusCode === 401 ||
  (error.statusCode === 400 &&
    error.result?.errors?.[0]?.extensions.code === 'UNAUTHENTICATED')

export const retry = async (error: ResponseError, _operation: Operation) => {
  if (checkStatusCodesRefresh(error)) {
    try {
      const userAndToken = await getUserAndToken()
      updateUserAndToken(userAndToken)
      const oldContext = _operation.getContext()
      _operation.setContext({
        ...oldContext,
        headers: {
          Authorization: `Bearer ${userAndToken.jwt}`,
        },
      })
      return true
    } catch (e) {
      return false
    }
  } else if (error.statusCode >= 500) {
    return true
  }
  return false
}

const httpLink = createHttpLink({
  uri: Config.bffUrl,
  fetch,
})

const httpLinkForOneGraph = createHttpLink({
  uri: Config.oneGraphUrl,
  fetch,
  headers: {
    [APOLLOGRAPH_CLIENT_NAME]: Config.siteName,
  },
})

const MAX_RETRIES = 3
// Only runs for network errors
const retryLink = new RetryLink({
  delay: (count) => {
    const isFetchingToken =
      getSessionStorageItem(SESSION_STORAGE_KEYS.fetchingToken) === 'true'
    const initialDelay = isFetchingToken ? 1000 : 300
    const exponentialBackoff = 2 ** (count - 1)

    return initialDelay * exponentialBackoff
  },
  attempts: (count, operation, error) => {
    const isFetchingToken =
      getSessionStorageItem(SESSION_STORAGE_KEYS.fetchingToken) === 'true'
    return isFetchingToken || (count <= MAX_RETRIES && retry(error, operation))
  },
})

// Not using retryLinkForOneGraph anymore as it is not handling fetching token on 401 and 400 calls
// export const retryLinkForOneGraph = new RetryLink({
//   delay: (count) => {
//     const initialDelay = 300
//     const exponentialBackoff = 2 ** (count - 1)

//     return initialDelay * exponentialBackoff
//   },
//   attempts: (count) => {
//     return count <= MAX_RETRIES
//   },
// })

// This will handle all graphQL errors and on getting UNAUTHENTICATED graphql error we will retry request once after fetching token
const errorLink = onError(({ response, operation, forward }) => {
  if (!response?.errors) return;

  const shouldRetry = response.errors.some(
    (error) => error.extensions.code === 'UNAUTHENTICATED'
  );
  
  if (!shouldRetry) return;

  return new Observable((observer) => {
    getUserAndToken()
      .then((newToken) => {
        updateUserAndToken(newToken);

        // Modify the operation context with the new token
        const oldHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...oldHeaders,
            [CHEGG_UCF_WRITING_TOKEN]: newToken.jwt,
          },
        });

        forward(operation).subscribe(observer);
      })
      .catch(observer.error.bind(observer));
  }) as unknown as ReturnType<ErrorHandler>;
});

const authLink = new ApolloLink((operation, forward) => {
  const token = getToken()
  const updatedHeaders = {}
  if (token) {
    // eslint-disable-next-line dot-notation
    updatedHeaders['Authorization'] = token
  }
  operation.setContext(({ headers }) => ({
    headers: {
      ...headers,
      ...updatedHeaders,
    },
  }))
  return forward(operation)
})

const authLinkForOneGraph = new ApolloLink((operation, forward) => {
  const token = getToken()
  operation.setContext(({ headers }) => ({
    headers: {
      ...headers,
      [CHEGG_UCF_WRITING_TOKEN]: token ? `${token}` : '',
      'chegg-ucf-model': getCookie(Cookies.model),
    },
  }))
  return forward(operation)
})

const bffLink = ApolloLink.from([authLink, retryLink, httpLink])
const oneGraphLink = ApolloLink.from([
  authLinkForOneGraph,
  errorLink,
  retryLink,
  httpLinkForOneGraph,
])

// Default will be bff if clientName is not passed/matched
const link = ApolloLink.split(
  (operation) => operation.getContext().clientName === 'oneGraph',
  oneGraphLink,
  bffLink
)

export const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: !(isProduction() && isProdByNodeEnv()),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
  },
})

export default withApollo(client)
